import { useMemo } from 'react';

import { InventoryAction, type CameraInfo } from '@scaleaq/sitecontrol-lib';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Minus,
  Plus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RadialControlPad, type RadialSectorItem } from '@/components';
import {
  RadialControlsHost,
  RadialControlsPad,
} from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraRadialControls/cameraRadialControls.style.ts';
import { useCameraStreamFrameControlsContext } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraStreamFrameControlsProvider/CameraStreamFrameControlsContext.ts';
import type { CameraAndWinchMovement } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/useCameraAndWinchCommandHooks.ts';

type CameraRadialControlsProps = {
  camera: CameraInfo;
  /** When false, pan/tilt sectors are omitted (same gate as gamepad/hotkeys). */
  canRotate: boolean;
  /** When false, center zoom half-circles are omitted. */
  canZoom: boolean;
  hasWinch: boolean;
  zIndex: number;
  /** Hold-repeat interval; must match keyboard/gamepad `callInterval` on the same widget. */
  holdRepeatDelayMs: number;
  movement: CameraAndWinchMovement;
  isWinchUnavailable?: boolean;
};

export const CameraRadialControls = ({
  camera,
  canRotate,
  canZoom,
  hasWinch,
  zIndex,
  holdRepeatDelayMs,
  movement,
  isWinchUnavailable = false,
}: CameraRadialControlsProps) => {
  const { t } = useTranslation(['common']);
  const { frameControlsVisible, setPauseHideTimer } = useCameraStreamFrameControlsContext();

  const {
    onPanCameraLeft,
    onPanCameraRight,
    onTiltCameraDown,
    onTiltCameraUp,
    onStopCameraRotation,
    onMoveWinchIn,
    onMoveWinchOut,
    onMoveWinchUp,
    onMoveWinchDown,
    onMoveWinchStop,
    onZoomCameraIn,
    onZoomCameraOut,
  } = movement;

  const cameraSectors = useMemo<RadialSectorItem[] | undefined>(() => {
    if (!canRotate) {
      return undefined;
    }

    return [
      {
        id: 'camera-up',
        label: t('shortcuts.camera.tiltCameraUp'),
        icon: ChevronUp,
        onStart: onTiltCameraUp,
        onStop: onStopCameraRotation,
      },
      {
        id: 'camera-right',
        label: t('shortcuts.camera.panCameraRight'),
        icon: ChevronRight,
        onStart: onPanCameraRight,
        onStop: onStopCameraRotation,
      },
      {
        id: 'camera-down',
        label: t('shortcuts.camera.tiltCameraDown'),
        icon: ChevronDown,
        onStart: onTiltCameraDown,
        onStop: onStopCameraRotation,
      },
      {
        id: 'camera-left',
        label: t('shortcuts.camera.panCameraLeft'),
        icon: ChevronLeft,
        onStart: onPanCameraLeft,
        onStop: onStopCameraRotation,
      },
    ];
  }, [
    canRotate,
    onPanCameraLeft,
    onPanCameraRight,
    onStopCameraRotation,
    onTiltCameraDown,
    onTiltCameraUp,
    t,
  ]);

  const winchSectors = useMemo<RadialSectorItem[] | undefined>(() => {
    if (!hasWinch) return undefined;
    const disabled = isWinchUnavailable;
    return [
      {
        id: 'winch-up',
        label: t('shortcuts.winch.moveWinchUp'),
        icon: ArrowUp,
        onStart: onMoveWinchUp,
        onStop: onMoveWinchStop,
        disabled,
      },
      {
        id: 'winch-out',
        label: t('shortcuts.winch.moveWinchOut'),
        icon: ArrowRight,
        onStart: onMoveWinchOut,
        onStop: onMoveWinchStop,
        disabled,
      },
      {
        id: 'winch-down',
        label: t('shortcuts.winch.moveWinchDown'),
        icon: ArrowDown,
        onStart: onMoveWinchDown,
        onStop: onMoveWinchStop,
        disabled,
      },
      {
        id: 'winch-in',
        label: t('shortcuts.winch.moveWinchIn'),
        icon: ArrowLeft,
        onStart: onMoveWinchIn,
        onStop: onMoveWinchStop,
        disabled,
      },
    ];
  }, [
    hasWinch,
    onMoveWinchDown,
    onMoveWinchIn,
    onMoveWinchOut,
    onMoveWinchStop,
    onMoveWinchUp,
    t,
    isWinchUnavailable,
  ]);

  const centerSectors = useMemo<RadialSectorItem[] | undefined>(() => {
    if (!canZoom) {
      return undefined;
    }

    const hasZoomIn = camera.privateActions.includes(InventoryAction.CameraZoomIn);
    const hasZoomOut = camera.privateActions.includes(InventoryAction.CameraZoomOut);

    if (!hasZoomIn && !hasZoomOut) {
      return undefined;
    }

    return [
      {
        id: 'camera-zoom-in',
        label: t('shortcuts.camera.zoomCameraIn'),
        icon: Plus,
        disabled: !hasZoomIn,
        onStart: onZoomCameraIn,
        onStop: onStopCameraRotation,
      },
      {
        id: 'camera-zoom-out',
        label: t('shortcuts.camera.zoomCameraOut'),
        icon: Minus,
        disabled: !hasZoomOut,
        onStart: onZoomCameraOut,
        onStop: onStopCameraRotation,
      },
    ];
  }, [camera.privateActions, canZoom, onStopCameraRotation, onZoomCameraIn, onZoomCameraOut, t]);

  const innerSectors = canRotate ? cameraSectors : winchSectors;
  const outerSectors = canRotate && hasWinch ? winchSectors : undefined;

  if (innerSectors == null && centerSectors == null) {
    return null;
  }

  return (
    <RadialControlsHost $zIndex={zIndex} $visible={frameControlsVisible}>
      <RadialControlsPad
        $visible={frameControlsVisible}
        onPointerDown={() => setPauseHideTimer(true)}
        onPointerUp={() => setPauseHideTimer(false)}
        onPointerCancel={() => setPauseHideTimer(false)}
      >
        <RadialControlPad
          innerSectors={innerSectors ?? []}
          outerSectors={outerSectors}
          centerSectors={centerSectors}
          disabled={!frameControlsVisible}
          holdRepeatDelayMs={holdRepeatDelayMs}
        />
      </RadialControlsPad>
    </RadialControlsHost>
  );
};
